import { useState } from 'react';
import {
  Button,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Select,
  MenuItem,
  FormControl,
  TextareaAutosize,
  InputLabel,
} from '@mui/material';

import { useCreateCategoryMutation } from 'src/api/categories/createCategory';
import { useUpdateCategoryMutation } from 'src/api/categories/updateCategory';
import { LanguageCodeFormSelect } from 'src/components/form/LanguageCodeFormSelect';
import { useGlobalFiltersStore } from 'src/store/globalFilters.store';

interface Props {
  open: boolean;
  category: any;
  onClose: () => void;
}

export default function CategoriesDialog({ onClose, ...props }: Props) {
  const { languageCode } = useGlobalFiltersStore();

  const defaultState = {
    name: '',
    difficulty: 0,
    explanation: '',
    languageCode,
  };

  const [category, setCategory] = useState(props.category ?? defaultState);

  const { mutateAsync: triggerCreate, isPending: isCreatePending } =
    useCreateCategoryMutation();
  const { mutateAsync: triggerUpdate, isPending: isUpdatePending } =
    useUpdateCategoryMutation();

  const saveCategory = async (category: any) => {
    category.name = category.name.trim();
    category.explanation = category.explanation.trim();

    if (category.id) {
      await triggerUpdate(category);
      return;
    }

    await triggerCreate(category);
  };

  const handleNameChange = (e: any) => {
    setCategory((prevState: any) => ({
      ...prevState,
      name: e.target.value,
    }));
  };

  const handleDifficultyChange = (e: any) => {
    setCategory((prevState: any) => ({
      ...prevState,
      difficulty: e.target.value,
    }));
  };

  const handleExplanationChange = (e: any) => {
    setCategory((prevState: any) => ({
      ...prevState,
      explanation: e.target.value,
    }));
  };

  const onSuccess = async () => {
    await saveCategory(category);
    setCategory(defaultState);
    onClose();
  };

  const onCancel = () => {
    setCategory(defaultState);
    onClose();
  };

  return (
    <Dialog
      open={props.open}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      <DialogTitle id='alert-dialog-title'>
        {category?.id ? 'Edit' : 'Create'} Category
      </DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%' }}>
          <Grid container sx={{ mt: 1, gap: 2 }}>
            {/* Row 1 */}
            <Grid container gap={2}>
              {/* Name */}
              <Grid item xs={8}>
                <TextField
                  id='name'
                  label='Name'
                  value={category.name}
                  onChange={handleNameChange}
                  sx={{ width: '100%' }}
                />
              </Grid>
              {/* Difficulty */}
              <Grid item xs={3}>
                <FormControl sx={{ width: '100%' }}>
                  <InputLabel id='difficulty'>Difficulty</InputLabel>
                  <Select
                    id='difficulty'
                    label='Difficulty'
                    value={category.difficulty}
                    onChange={handleDifficultyChange}
                  >
                    {Array.from({ length: 4 }, (_, i) => i + 1).map((item) => (
                      <MenuItem key={item} value={item}>
                        {item}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Row 2 */}
            <LanguageCodeFormSelect
              defaultValue={languageCode}
              value={category.languageCode}
              onChange={(e) => {
                setCategory({ ...category, languageCode: e.target.value });
              }}
            />

            {/* Row 3 */}
            <FormControl sx={{ width: '100%' }}>
              <TextareaAutosize
                minRows={3}
                style={{ width: '100%', padding: '10px' }}
                id='explanation'
                placeholder='Explanation'
                value={category.explanation}
                onChange={handleExplanationChange}
              />
            </FormControl>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} variant='outlined'>
          Cancel
        </Button>
        <Button
          onClick={onSuccess}
          autoFocus
          disabled={isCreatePending || isUpdatePending}
          variant='contained'
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
